import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const themes = {
  ocean_serenity: {
    name: "Ocean Serenity",
    primary: "#006D77",
    secondary: "#D4AF37",
    background: "#F4F1DE",
    foreground: "#0F1C2E",
    muted: "#E0FBFC",
    accent: "#83C5BE",
    border: "rgba(0, 109, 119, 0.2)",
    cardBg: "#FFFFFF",
    cardText: "#0F1C2E",
    isDark: false
  },
  forest_grounding: {
    name: "Forest Grounding",
    primary: "#2D6A4F",
    secondary: "#D4A373",
    background: "#FEFAE0",
    foreground: "#1B4332",
    muted: "#D8F3DC",
    accent: "#B7E4C7",
    border: "rgba(45, 106, 79, 0.2)",
    cardBg: "#FFFFFF",
    cardText: "#1B4332",
    isDark: false
  },
  sunset_vitality: {
    name: "Sunset Vitality",
    primary: "#E07A5F",
    secondary: "#3D405B",
    background: "#F2CC8F",
    foreground: "#3D405B",
    muted: "#F4F1DE",
    accent: "#81B29A",
    border: "rgba(224, 122, 95, 0.2)",
    cardBg: "#FFFFFF",
    cardText: "#3D405B",
    isDark: false
  },
  mountain_clarity: {
    name: "Mountain Clarity",
    primary: "#457B9D",
    secondary: "#1D3557",
    background: "#F1FAEE",
    foreground: "#1D3557",
    muted: "#A8DADC",
    accent: "#E63946",
    border: "rgba(69, 123, 157, 0.2)",
    cardBg: "#FFFFFF",
    cardText: "#1D3557",
    isDark: false
  },
  midnight_zen: {
    name: "Midnight Zen",
    primary: "#64B5F6",
    secondary: "#FFB703",
    background: "#0A1929",
    foreground: "#F8F9FA",
    muted: "#1E3A5F",
    accent: "#FB8500",
    border: "rgba(100, 181, 246, 0.3)",
    cardBg: "#132F4C",
    cardText: "#F8F9FA",
    isDark: true
  }
};

// Default 3D effect settings
const default3DEffects = {
  enabled: true,
  intensity: 50, // 0-100
  textShadowIntensity: 60, // 0-100
  cardShadowIntensity: 70, // 0-100
  buttonShadowIntensity: 80, // 0-100
  shadowColor: '#000000',
  highlightColor: '#FFFFFF',
  shadowAngle: 135, // degrees
  shadowDistance: 4, // pixels base
  enableTextEmboss: true,
  enableCardDepth: true,
  enableButtonDepth: true,
  enableSectionDepth: true,
};

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [activeTheme, setActiveTheme] = useState('ocean_serenity');
  const [headerOpacity, setHeaderOpacity] = useState(95);
  const [customColors, setCustomColors] = useState(null);
  // Admin panel specific theme
  const [adminTheme, setAdminTheme] = useState('ocean_serenity');
  const [adminCustomColors, setAdminCustomColors] = useState(null);
  // 3D Effects
  const [effects3D, setEffects3D] = useState(default3DEffects);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTheme();
  }, []);

  // Apply theme colors whenever activeTheme or customColors change
  useEffect(() => {
    applyThemeColors(activeTheme, customColors);
  }, [activeTheme, customColors]);

  // Apply 3D effects whenever they change
  useEffect(() => {
    apply3DEffects(effects3D);
  }, [effects3D]);

  const fetchTheme = async () => {
    try {
      const response = await axios.get(`${API}/theme`);
      if (response.data?.active_theme) {
        setActiveTheme(response.data.active_theme);
      }
      if (response.data?.header_opacity !== undefined) {
        // Ensure minimum opacity of 65%
        setHeaderOpacity(Math.max(65, response.data.header_opacity));
      }
      if (response.data?.custom_colors) {
        setCustomColors(response.data.custom_colors);
      }
      // Admin theme settings
      if (response.data?.admin_theme) {
        setAdminTheme(response.data.admin_theme);
      }
      if (response.data?.admin_custom_colors) {
        setAdminCustomColors(response.data.admin_custom_colors);
      }
      // 3D Effects settings
      if (response.data?.effects_3d) {
        setEffects3D({ ...default3DEffects, ...response.data.effects_3d });
      }
    } catch (error) {
      console.log('Using default theme');
    } finally {
      setLoading(false);
    }
  };

  const changeTheme = async (themeName) => {
    setActiveTheme(themeName);
    applyThemeColors(themeName, customColors);
  };

  const applyThemeColors = (themeName, colors) => {
    const baseTheme = themes[themeName] || themes.ocean_serenity;
    const theme = colors ? { ...baseTheme, ...colors } : baseTheme;
    
    document.documentElement.style.setProperty('--theme-primary', theme.primary);
    document.documentElement.style.setProperty('--theme-secondary', theme.secondary);
    document.documentElement.style.setProperty('--theme-background', theme.background);
    document.documentElement.style.setProperty('--theme-foreground', theme.foreground);
    document.documentElement.style.setProperty('--theme-muted', theme.muted);
    document.documentElement.style.setProperty('--theme-accent', theme.accent);
    document.documentElement.style.setProperty('--theme-border', theme.border);
    document.documentElement.style.setProperty('--theme-card-bg', theme.cardBg);
    document.documentElement.style.setProperty('--theme-card-text', theme.cardText);
  };

  // Apply 3D effects to CSS variables
  const apply3DEffects = (settings) => {
    if (!settings.enabled) {
      // Disable all 3D effects
      document.documentElement.style.setProperty('--3d-text-shadow', 'none');
      document.documentElement.style.setProperty('--3d-card-shadow', 'none');
      document.documentElement.style.setProperty('--3d-button-shadow', 'none');
      document.documentElement.style.setProperty('--3d-section-shadow', 'none');
      document.documentElement.style.setProperty('--3d-heading-shadow', 'none');
      document.documentElement.style.setProperty('--3d-transform', 'none');
      return;
    }

    const intensity = settings.intensity / 100;
    const textIntensity = settings.textShadowIntensity / 100;
    const cardIntensity = settings.cardShadowIntensity / 100;
    const buttonIntensity = settings.buttonShadowIntensity / 100;
    const distance = settings.shadowDistance * intensity;
    const angle = settings.shadowAngle * (Math.PI / 180);
    
    // Calculate shadow offsets based on angle
    const xOffset = Math.round(Math.cos(angle) * distance);
    const yOffset = Math.round(Math.sin(angle) * distance);
    
    // Parse colors for transparency
    const shadowColor = settings.shadowColor;
    const highlightColor = settings.highlightColor;
    
    // Text emboss effect (multiple layered shadows)
    if (settings.enableTextEmboss) {
      const textShadow = `
        ${xOffset * 0.5}px ${yOffset * 0.5}px ${distance}px ${shadowColor}${Math.round(textIntensity * 80).toString(16).padStart(2, '0')},
        ${-xOffset * 0.3}px ${-yOffset * 0.3}px ${distance * 0.5}px ${highlightColor}${Math.round(textIntensity * 40).toString(16).padStart(2, '0')},
        ${xOffset}px ${yOffset}px ${distance * 2}px ${shadowColor}${Math.round(textIntensity * 40).toString(16).padStart(2, '0')}
      `.trim();
      document.documentElement.style.setProperty('--3d-text-shadow', textShadow);
      
      // Stronger heading shadow
      const headingShadow = `
        ${xOffset}px ${yOffset}px ${distance * 1.5}px ${shadowColor}${Math.round(textIntensity * 90).toString(16).padStart(2, '0')},
        ${-xOffset * 0.5}px ${-yOffset * 0.5}px ${distance}px ${highlightColor}${Math.round(textIntensity * 50).toString(16).padStart(2, '0')},
        ${xOffset * 1.5}px ${yOffset * 1.5}px ${distance * 3}px ${shadowColor}${Math.round(textIntensity * 50).toString(16).padStart(2, '0')},
        0 0 ${distance * 4}px ${shadowColor}${Math.round(textIntensity * 20).toString(16).padStart(2, '0')}
      `.trim();
      document.documentElement.style.setProperty('--3d-heading-shadow', headingShadow);
    } else {
      document.documentElement.style.setProperty('--3d-text-shadow', 'none');
      document.documentElement.style.setProperty('--3d-heading-shadow', 'none');
    }
    
    // Card depth effect (layered box shadows)
    if (settings.enableCardDepth) {
      const cardShadow = `
        ${xOffset}px ${yOffset}px ${distance * 2}px ${shadowColor}${Math.round(cardIntensity * 30).toString(16).padStart(2, '0')},
        ${xOffset * 2}px ${yOffset * 2}px ${distance * 4}px ${shadowColor}${Math.round(cardIntensity * 20).toString(16).padStart(2, '0')},
        ${xOffset * 3}px ${yOffset * 3}px ${distance * 8}px ${shadowColor}${Math.round(cardIntensity * 15).toString(16).padStart(2, '0')},
        inset ${-xOffset * 0.5}px ${-yOffset * 0.5}px ${distance}px ${highlightColor}${Math.round(cardIntensity * 10).toString(16).padStart(2, '0')}
      `.trim();
      document.documentElement.style.setProperty('--3d-card-shadow', cardShadow);
    } else {
      document.documentElement.style.setProperty('--3d-card-shadow', 'none');
    }
    
    // Button depth effect
    if (settings.enableButtonDepth) {
      const buttonShadow = `
        ${xOffset}px ${yOffset}px ${distance}px ${shadowColor}${Math.round(buttonIntensity * 50).toString(16).padStart(2, '0')},
        ${xOffset * 1.5}px ${yOffset * 1.5}px ${distance * 2}px ${shadowColor}${Math.round(buttonIntensity * 30).toString(16).padStart(2, '0')},
        inset ${-xOffset * 0.3}px ${-yOffset * 0.3}px ${distance * 0.5}px ${highlightColor}${Math.round(buttonIntensity * 30).toString(16).padStart(2, '0')}
      `.trim();
      document.documentElement.style.setProperty('--3d-button-shadow', buttonShadow);
    } else {
      document.documentElement.style.setProperty('--3d-button-shadow', 'none');
    }
    
    // Section depth effect
    if (settings.enableSectionDepth) {
      const sectionShadow = `
        ${xOffset * 2}px ${yOffset * 2}px ${distance * 6}px ${shadowColor}${Math.round(cardIntensity * 15).toString(16).padStart(2, '0')},
        ${xOffset * 4}px ${yOffset * 4}px ${distance * 12}px ${shadowColor}${Math.round(cardIntensity * 10).toString(16).padStart(2, '0')}
      `.trim();
      document.documentElement.style.setProperty('--3d-section-shadow', sectionShadow);
    } else {
      document.documentElement.style.setProperty('--3d-section-shadow', 'none');
    }
    
    // Store intensity for use in components
    document.documentElement.style.setProperty('--3d-intensity', intensity.toString());
  };

  const change3DEffects = (newEffects) => {
    const merged = { ...effects3D, ...newEffects };
    setEffects3D(merged);
    apply3DEffects(merged);
  };

  const changeHeaderOpacity = (opacity) => {
    // Ensure minimum opacity of 65%
    setHeaderOpacity(Math.max(65, opacity));
  };

  const changeCustomColors = (colors) => {
    setCustomColors(colors);
    applyThemeColors(activeTheme, colors);
  };

  const changeAdminTheme = (themeName) => {
    setAdminTheme(themeName);
  };

  const changeAdminCustomColors = (colors) => {
    setAdminCustomColors(colors);
  };

  // Get current theme with custom colors applied
  const baseTheme = themes[activeTheme] || themes.ocean_serenity;
  const currentTheme = customColors ? { ...baseTheme, ...customColors } : baseTheme;

  // Get admin theme with custom colors applied
  const baseAdminTheme = themes[adminTheme] || themes.ocean_serenity;
  const currentAdminTheme = adminCustomColors ? { ...baseAdminTheme, ...adminCustomColors } : baseAdminTheme;

  return (
    <ThemeContext.Provider value={{ 
      activeTheme, 
      currentTheme, 
      themes, 
      changeTheme,
      headerOpacity,
      changeHeaderOpacity,
      customColors,
      changeCustomColors,
      // Admin theme
      adminTheme,
      currentAdminTheme,
      changeAdminTheme,
      adminCustomColors,
      changeAdminCustomColors,
      // 3D Effects
      effects3D,
      change3DEffects,
      default3DEffects,
      loading 
    }}>
      <div className={`theme-${activeTheme}`}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export { themes };
